import axios from 'axios';

const INVALID_JWT_MESSAGE = 'Invalid JWT provided';

/**
 * Set the appropriate Authorization header on the request
 *
 * @param {AxiosRequestConfig} request Request to apply configuration on
 * @param {boolean} override Whether or not to overwrite an existing Authorization header
 * @return {AxiosRequestConfig} Updated request instance
 */
function setAuthorization(request, override) {
  const token = localStorage.getItem('accessToken');
  if (token && (override || !('Authorization' in request.headers))) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  return request;
}

/**
 * Creates an Axios instance with the specified service name that points to the specified
 * endpoint
 *
 * @param {Object} serviceLocation Location (base URL) of service
 * @return {AxiosInstance} Constructed Axios instance
 */
export function createAxioInstance(serviceLocation) {
  const instance = axios.create({
    baseURL: serviceLocation
  });

  // Request interceptor to add Authorization header to requests
  instance.interceptors.request.use((request) => setAuthorization(request, false));

  // Response interceptor to handle invalid JWT & re-execute request
  instance.interceptors.response.use(
    (response) => response,
    (err) => {
      if (err.response && err.response.status === 401 &&
        err.response.data.error === INVALID_JWT_MESSAGE &&
        !err.response.config.preventRefresh) {
        return Vue.prototype.$auth.refresh()
          // If the refresh call fails, it will redirect the page to somewhere else
          // Therefore timeout this request indefinitely so that we don't end up presenting
          // errors to the user
          .catch(() => new Promise(() => {
          })).then(() => {
            const secondRequest = Object.assign(
              { preventRefresh: true },
              err.response.config
            );

            return instance.request(setAuthorization(secondRequest, true));
          });
      } else {
        return Promise.reject(err);
      }
    }
  );

  // Add a function to create a WS connection to this service
  instance.socket = function(target) {
    return new WebSocket(serviceLocation.replace(/^http/, 'ws') + target);
  };

  return instance;
}

/**
 * Helper method to ensure services object is initialized on Vue
 *
 * @param {Object} Vue Vue instance to initialize
 */
function createServicesObject(Vue) {
  if (!Vue.services) {
    Vue.services = {};
  }
  if (!Vue.prototype.$services) {
    Vue.prototype.$services = Vue.services;
  }
}

/**
 * Create a Vue installer that creates a service with the given name at specified location
 *
 * @param {String} serviceName Name to install service as
 * @param {Object} serviceLocation Location (base URL) of service
 * @return {Object} Vue installer object
 */
export default function(serviceName, serviceLocation) {
  return {
    /**
     * Install service to Vue
     *
     * @param {Object} Vue The global Vue object
     */
    install(Vue) {
      createServicesObject(Vue);

      Vue.prototype.$services[serviceName] = createAxioInstance(serviceLocation);
    }
  };
}

/**
 * Create a Vue installer that creates a service with the given name with multiple API locations
 *
 * @param {String} serviceName Name to install service as
 * @param {Object} serviceLocations Locations (base URLs) of service
 * @return {Object} Vue installer object
 */
export function multiEndpointService(serviceName, serviceLocations) {
  return {
    install(Vue) {
      createServicesObject(Vue);

      Vue.prototype.$services[serviceName] = serviceLocations.map(createAxioInstance);
    }
  };
}
